import { useEffect, useState } from 'react';
import useSWR from 'swr';
import ModuleFilters, { TgetFiltersList } from '../../../../components/ModuleFilters/ModuleFilters';
import { getOrderFilters } from '../../../../services/orders';
import { listOrganizations } from '../../../../services/bussines';
import { getMonthFilterOptions } from '../../../../utils/formatDatesAndHours';
import { paymentMethodLabelsValue } from '../../../../utils/variablesToTranslate';
import useOrdersFinancesModule from '../OrdersListingFinances/useOrdersFinancesModule';
import { ISortFilter, SORT_ORDER } from 'types/global';
import './OrdersTrackingFinanceFilters.scss';

const OrdersTrackingFinanceFilters = () => {
  const { setSortFilter, orderFilters, setOrderFilters, sortFilter } = useOrdersFinancesModule();
  const [countriesOptions, setCountriesOptions] = useState<string[]>([]);
  const [openMonthFilter, setOpenMonthFilter] = useState<boolean>(false);
  const [openCompanyFilter, setOpenCompanyFilter] = useState<boolean>(false);
  const [openPaymentMethodFilter, setOpenPaymentMethodFilter] = useState<boolean>(false);
  const [openShipmentCountryFilter, setOpenShipmentCountryFilter] = useState<boolean>(false);
  const [openInvoiceFilter, setOpenInvoiceFilter] = useState<boolean>(false);
  const [openSortFilter, setOpenSortFilter] = useState<boolean>(false);

  const { data: organizationsData } = useSWR('organizations-list', listOrganizations, {
    revalidateOnFocus: false
  });

  const ordersTrackingFinanceSearchBarClassNames = {
    selectContainer: 'filterSearchContainerOrdersTrackingFinance',
    searchBarContainer: 'searchBarContainerCustomOrdersTrackingFinance',
    searchBar: 'filterSearchBarOrdersTrackingFinance',
    input: 'filterInputOrdersTrackingFinance'
  };

  const ordersFilterMapping = {
    Empresa: 'company',
    Mes: 'month',
    País: 'countryDestination',
    'Método pago': 'paymentType',
    Factura: 'invoice',
    Ordenar: 'sort'
  };

  const sortOptions = ['Número de la orden', 'Monto'];

  const sortOptionsToValueMap = new Map<string, string>([
    ['Número de la orden', 'orderId'],
    ['Monto', 'total']
  ]);

  const companyFilterRawOptions = organizationsData?.map(org => `${org.name}::${org.id}`) || [];
  const invoiceFilterRawOptions = ['Pendiente por cargar', 'Cargada'];
  const monthFilterOptions = getMonthFilterOptions();
  const monthFilterRawOptions = monthFilterOptions.map(opt => opt.label);
  const monthFilterLabelToValueMap = new Map(monthFilterOptions.map(opt => [opt.label, opt.value]));

  const companyFilterRawOptionsToNumberMap: Map<string, string> = organizationsData
    ? new Map(organizationsData?.map(org => [`${org.name}::${org.id}`, String(org.id)]))
    : new Map();

  const invoiceFilterRawOptionsToNumberMap: Map<string, string> = invoiceFilterRawOptions.reduce(
    (acc, invoice, index) => {
      acc.set(invoice, String(index + 1));
      return acc;
    },
    new Map<string, string>()
  );

  const paymentMethodRawOptionName = paymentMethodLabelsValue.map(item => item.name);

  const paymentMethodNameValue: Map<string, string> = new Map(
    paymentMethodLabelsValue?.map(type => [type.name, type.value])
  );

  const getOrdersFiltersList: TgetFiltersList = (formModuleFilters, handleClickFilterOption, getFilterActiveStatus) => {
    const companyOptions = formModuleFilters('Empresa', companyFilterRawOptions, companyFilterRawOptionsToNumberMap);
    const companyOptionsWithNameOnly = companyOptions.map(option => ({
      ...option,
      name: option.name.includes('::') ? option.name.split('::')[0] : option.name
    }));

    return [
      {
        title: 'Empresa',
        openFunction: setOpenCompanyFilter,
        openStatus: openCompanyFilter,
        rawSelectOptions: companyOptionsWithNameOnly,
        handleChangeSelectedOption: handleClickFilterOption('company', 'value'),
        isFilterActive: getFilterActiveStatus('company'),
        showSearch: true,
        showBubbleInformation: true
      },
      {
        title: 'Mes',
        openFunction: setOpenMonthFilter,
        openStatus: openMonthFilter,
        rawSelectOptions: formModuleFilters('Mes', monthFilterRawOptions, monthFilterLabelToValueMap),
        handleChangeSelectedOption: handleClickFilterOption('month', 'value'),
        isFilterActive: getFilterActiveStatus('month'),
        showSearch: true,
        searchPlaceholder: 'Buscador',
        showBubbleInformation: true
      },
      {
        title: 'País',
        openFunction: setOpenShipmentCountryFilter,
        openStatus: openShipmentCountryFilter,
        rawSelectOptions: formModuleFilters('País', countriesOptions),
        handleChangeSelectedOption: handleClickFilterOption('countryDestination', 'name'),
        isFilterActive: getFilterActiveStatus('countryDestination'),
        showSearch: true,
        searchPlaceholder: 'Buscador',
        sortAlphabetically: true,
        showBubbleInformation: true
      },
      {
        title: 'Factura',
        openFunction: setOpenInvoiceFilter,
        openStatus: openInvoiceFilter,
        rawSelectOptions: formModuleFilters('Factura', invoiceFilterRawOptions, invoiceFilterRawOptionsToNumberMap),
        handleChangeSelectedOption: handleClickFilterOption('invoice', 'value'),
        isFilterActive: getFilterActiveStatus('invoice'),
        showBubbleInformation: true
      },
      {
        title: 'Método pago',
        openFunction: setOpenPaymentMethodFilter,
        openStatus: openPaymentMethodFilter,
        rawSelectOptions: formModuleFilters('Método pago', paymentMethodRawOptionName, paymentMethodNameValue),
        handleChangeSelectedOption: handleClickFilterOption('paymentType', 'value'),
        isFilterActive: getFilterActiveStatus('paymentType'),
        showBubbleInformation: true
      },
      {
        title: 'Ordenar',
        openFunction: setOpenSortFilter,
        openStatus: openSortFilter,
        optionContainerCustomClass: 'optionContainerCustomClassOrdersTrackingFinance',
        rawSelectOptions: formModuleFilters('Ordenar', sortOptions, sortOptionsToValueMap),
        handleChangeSelectedOption: handleClickFilterOption('sort', 'value'),
        isFilterActive: getFilterActiveStatus('sort'),
        titleFilterContainer: 'Ordenar por:',
        useRadioButtons: true
      }
    ];
  };

  const getShipmentCountryFilterOptions = async () => {
    const response = await getOrderFilters();
    if (response?.countries) setCountriesOptions(response?.countries);
  };

  const handleSortChange = (sortFilter: ISortFilter) => {
    setSortFilter(sortFilter);
  };

  useEffect(() => {
    getShipmentCountryFilterOptions();
  }, []);

  useEffect(() => {
    if (!orderFilters.some(filter => filter.name === 'sort')) {
      setOrderFilters([
        ...orderFilters,
        {
          name: 'sort',
          includes: 'in',
          values: ['orderId']
        }
      ]);

      setSortFilter({ value: SORT_ORDER.DESC });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="ordersTrackingFinanceFilters">
      <ModuleFilters
        moduleFilters={orderFilters}
        setModuleFilters={setOrderFilters}
        getFiltersList={getOrdersFiltersList}
        moduleFiltersTitlesMapping={ordersFilterMapping}
        customGap={8}
        showSort
        showSortInFilter="Ordenar"
        onSortChange={handleSortChange}
        singleSelectFilters={{ sort: true, paymentType: true, invoice: true }}
        customHeightContainer="customHeightContainerOrdersTrackingFinance"
        showReferenceLetters
        customSortClassName="customSortClassNameOrdersTrackingFinance"
        currentSortFilter={sortFilter}
        searchBarClassNames={ordersTrackingFinanceSearchBarClassNames}
      />
    </div>
  );
};

export default OrdersTrackingFinanceFilters;
